import React, {
 createContext, useContext, useState, useCallback, useRef
} from 'react';
import * as THREE from 'three';
import type { ArcballControls as ArcballControlsImpl, OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { PCNode } from '../MineModel/Nodes';
import PCSpool from '../MineModel/Spools/PCSpool';
import { cadToThreeJsPosition } from '../Scenes/scene-utils';

interface MarqueeBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

type NodesSelectedHandler = (nodes: PCNode[], additive: boolean) => void;
type SpoolsSelectedHandler = (spools: PCSpool[], additive: boolean) => void;

interface MarqueeContextValue {
  isMarqueeMode: boolean;
  marqueeBox: MarqueeBox | null;
  setMarqueeMode: (enabled: boolean) => void;
  onNodesSelected: NodesSelectedHandler | null;
  setOnNodesSelected: (callback: NodesSelectedHandler | null) => void;
  onSpoolsSelected: SpoolsSelectedHandler | null;
  setOnSpoolsSelected: (callback: SpoolsSelectedHandler | null) => void;
  getNodesInBox: (nodes: Record<string, PCNode>, box: MarqueeBox) => PCNode[];
  getSpoolsInBox: (spools: Record<string, PCSpool>, box: MarqueeBox) => PCSpool[];
  handleMarqueeStart: (x: number, y: number) => void;
  handleMarqueeMove: (x: number, y: number) => void;
  handleMarqueeEnd: (nodes: Record<string, PCNode>, spools: Record<string, PCSpool> | undefined, additive: boolean) => void;
  cameraRef: React.RefObject<THREE.OrthographicCamera | null>;
  controlsRef: React.RefObject<ArcballControlsImpl | OrbitControlsImpl | null>;
  modelTransform: { offset: THREE.Vector3; scale: number } | null;
}

const MarqueeContext = createContext<MarqueeContextValue | null>(null);

interface MarqueeProviderProps {
  children: React.ReactNode;
  cameraRef: React.RefObject<THREE.OrthographicCamera | null>;
  controlsRef: React.RefObject<ArcballControlsImpl | OrbitControlsImpl | null>;
  modelTransform: { offset: THREE.Vector3; scale: number } | null;
}

export const MarqueeProvider: React.FC<MarqueeProviderProps> = ({ children, cameraRef, controlsRef, modelTransform }) => {
  const [isMarqueeMode, setIsMarqueeMode] = useState(false);
  const [marqueeBox, setMarqueeBox] = useState<MarqueeBox | null>(null);
  const startPointRef = useRef<{ x: number; y: number } | null>(null);
  const onNodesSelectedRef = useRef<NodesSelectedHandler | null>(null);
  const onSpoolsSelectedRef = useRef<SpoolsSelectedHandler | null>(null);

  const setMarqueeMode = useCallback((enabled: boolean) => {
    setIsMarqueeMode(enabled);
    if (!enabled) {
      setMarqueeBox(null);
      startPointRef.current = null;
    }
  }, []);

  const setOnNodesSelected = useCallback((callback: NodesSelectedHandler | null) => {
    onNodesSelectedRef.current = callback;
  }, []);

  const setOnSpoolsSelected = useCallback((callback: SpoolsSelectedHandler | null) => {
    onSpoolsSelectedRef.current = callback;
  }, []);

  const getNodesInBox = useCallback((
    nodes: Record<string, PCNode>,
    mBox: MarqueeBox,
  ): PCNode[] => {
    const camera = cameraRef.current;
    if (!camera) return [];

    const canvas = document.querySelector('canvas');
    if (!canvas) return [];

    const rect = canvas.getBoundingClientRect();
    const cssWidth = rect.width;
    const cssHeight = rect.height;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const scaleX = canvasWidth / cssWidth;
    const scaleY = canvasHeight / cssHeight;

    const minX = Math.min(mBox.startX, mBox.endX) * scaleX;
    const maxX = Math.max(mBox.startX, mBox.endX) * scaleX;
    const minY = Math.min(mBox.startY, mBox.endY) * scaleY;
    const maxY = Math.max(mBox.startY, mBox.endY) * scaleY;

    // Project against the camera exactly as the renderer left it. Do NOT call
    // controls.update() here: with OrbitControls (z-up mode) it re-derives the
    // camera from its spherical state and nudges it off the rendered position,
    // which offsets the marquee selection from what the user sees.
    if (!modelTransform) return [];

    const { offset, scale } = modelTransform;
    const nodesList = Object.values(nodes);
    const worldPos = new THREE.Vector3();

    return nodesList.filter((node) => {
      worldPos.copy(cadToThreeJsPosition(node.coords));
      worldPos.add(offset).multiplyScalar(scale);
      worldPos.project(camera);
      const x = ((worldPos.x + 1) / 2) * canvasWidth;
      const y = ((1 - worldPos.y) / 2) * canvasHeight;

      return (
        x >= minX
        && x <= maxX
        && y >= minY
        && y <= maxY
      );
    });
  }, [cameraRef, controlsRef, modelTransform]);

  const getSpoolsInBox = useCallback((
    spools: Record<string, PCSpool>,
    mBox: MarqueeBox,
  ): PCSpool[] => {
    const camera = cameraRef.current;
    if (!camera || !modelTransform) return [];

    const canvas = document.querySelector('canvas');
    if (!canvas) return [];

    const rect = canvas.getBoundingClientRect();
    const cssWidth = rect.width;
    const cssHeight = rect.height;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const scaleX = canvasWidth / cssWidth;
    const scaleY = canvasHeight / cssHeight;

    const minX = Math.min(mBox.startX, mBox.endX) * scaleX;
    const maxX = Math.max(mBox.startX, mBox.endX) * scaleX;
    const minY = Math.min(mBox.startY, mBox.endY) * scaleY;
    const maxY = Math.max(mBox.startY, mBox.endY) * scaleY;

    // Project against the camera as the renderer left it — see getNodesInBox.
    const { offset, scale } = modelTransform;
    const worldPos = new THREE.Vector3();

    const projectToScreen = (node: PCNode): { x: number; y: number } => {
      worldPos.copy(cadToThreeJsPosition(node.coords));
      worldPos.add(offset).multiplyScalar(scale);
      worldPos.project(camera);
      return {
        x: ((worldPos.x + 1) / 2) * canvasWidth,
        y: ((1 - worldPos.y) / 2) * canvasHeight,
      };
    };

    const isPointInBox = (px: number, py: number): boolean => (
      px >= minX && px <= maxX && py >= minY && py <= maxY
    );

    const lineIntersectsBox = (
      x1: number, y1: number, x2: number, y2: number,
    ): boolean => {
      if (isPointInBox(x1, y1) || isPointInBox(x2, y2)) return true;

      const intersectsSegment = (
        ax: number, ay: number, bx: number, by: number,
        cx: number, cy: number, dx: number, dy: number,
      ): boolean => {
        const denom = (dy - cy) * (bx - ax) - (dx - cx) * (by - ay);
        if (Math.abs(denom) < 1e-10) return false;
        const ua = ((dx - cx) * (ay - cy) - (dy - cy) * (ax - cx)) / denom;
        const ub = ((bx - ax) * (ay - cy) - (by - ay) * (ax - cx)) / denom;
        return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
      };

      return (
        intersectsSegment(x1, y1, x2, y2, minX, minY, maxX, minY)
        || intersectsSegment(x1, y1, x2, y2, maxX, minY, maxX, maxY)
        || intersectsSegment(x1, y1, x2, y2, maxX, maxY, minX, maxY)
        || intersectsSegment(x1, y1, x2, y2, minX, maxY, minX, minY)
      );
    };

    return Object.values(spools).filter((spool) => {
      const pointA = projectToScreen(spool.pointA);
      const pointB = projectToScreen(spool.pointB);
      return lineIntersectsBox(pointA.x, pointA.y, pointB.x, pointB.y);
    });
  }, [cameraRef, controlsRef, modelTransform]);

  const handleMarqueeStart = useCallback((x: number, y: number) => {
    if (!isMarqueeMode) return;
    startPointRef.current = { x, y };
    setMarqueeBox({ startX: x, startY: y, endX: x, endY: y });
  }, [isMarqueeMode]);

  const handleMarqueeMove = useCallback((x: number, y: number) => {
    if (!isMarqueeMode || !startPointRef.current) return;
    setMarqueeBox({
      startX: startPointRef.current.x,
      startY: startPointRef.current.y,
      endX: x,
      endY: y,
    });
  }, [isMarqueeMode]);

  const handleMarqueeEnd = useCallback((
    nodes: Record<string, PCNode>,
    spools: Record<string, PCSpool> | undefined,
    additive: boolean,
  ) => {
    if (!isMarqueeMode || !marqueeBox) return;

    const marqueeWidth = Math.abs(marqueeBox.endX - marqueeBox.startX);
    const marqueeHeight = Math.abs(marqueeBox.endY - marqueeBox.startY);

    if (marqueeWidth > 5 && marqueeHeight > 5) {
      const nodesInBox = getNodesInBox(nodes, marqueeBox);
      onNodesSelectedRef.current?.(nodesInBox, additive);

      if (spools && onSpoolsSelectedRef.current) {
        const spoolsInBox = getSpoolsInBox(spools, marqueeBox);
        onSpoolsSelectedRef.current(spoolsInBox, additive);
      }
    }

    setMarqueeBox(null);
    startPointRef.current = null;
  }, [isMarqueeMode, marqueeBox, getNodesInBox, getSpoolsInBox]);

  return (
    <MarqueeContext.Provider
      value={{
        isMarqueeMode,
        marqueeBox,
        setMarqueeMode,
        onNodesSelected: onNodesSelectedRef.current,
        setOnNodesSelected,
        onSpoolsSelected: onSpoolsSelectedRef.current,
        setOnSpoolsSelected,
        getNodesInBox,
        getSpoolsInBox,
        handleMarqueeStart,
        handleMarqueeMove,
        handleMarqueeEnd,
        cameraRef,
        controlsRef,
        modelTransform,
      }}
    >
      {children}
    </MarqueeContext.Provider>
  );
};

export const useMarquee = () => {
  const context = useContext(MarqueeContext);
  if (!context) {
    throw new Error('useMarquee must be used within a MarqueeProvider');
  }
  return context;
};

export default MarqueeContext;
